/**
 * ASF (Acclaim Skeleton File) Parser
 *
 * Parses CMU Motion Capture Lab ASF files into a Skeleton structure.
 * Format: sections prefixed with `:` — :units, :root, :bonedata, :hierarchy
 */

export interface BoneData {
  name: string;
  direction: [number, number, number];
  length: number;
  axis: [number, number, number];
  axisOrder: string;
  dof: string[];
}

export interface Skeleton {
  lengthScale: number;
  root: { position: [number, number, number]; orientation: [number, number, number] };
  bones: Map<string, BoneData>;
  hierarchy: Map<string, string[]>;
}

export function parseASF(text: string): Skeleton {
  const lines = text.split('\n');
  let i = 0;

  let lengthScale = 1;
  const root = { position: [0, 0, 0] as [number, number, number], orientation: [0, 0, 0] as [number, number, number] };
  const bones = new Map<string, BoneData>();
  const hierarchy = new Map<string, string[]>();

  while (i < lines.length) {
    const line = lines[i].trim();

    if (line === ':units') {
      i++;
      while (i < lines.length && !lines[i].trim().startsWith(':')) {
        const uLine = lines[i].trim();
        const parts = uLine.split(/\s+/);
        if (parts[0] === 'length') {
          lengthScale = parseFloat(parts[1]);
        }
        i++;
      }
      continue;
    }

    if (line === ':root') {
      i++;
      while (i < lines.length && !lines[i].trim().startsWith(':')) {
        const rLine = lines[i].trim();
        const parts = rLine.split(/\s+/);
        if (parts[0] === 'position') {
          root.position = [parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3])];
        } else if (parts[0] === 'orientation') {
          root.orientation = [parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3])];
        }
        i++;
      }
      continue;
    }

    if (line === ':bonedata') {
      i++;
      while (i < lines.length && !lines[i].trim().startsWith(':')) {
        const bLine = lines[i].trim();
        if (bLine === 'begin') {
          i++;
          let name = '';
          let direction: [number, number, number] = [0, 0, 0];
          let length = 0;
          let axis: [number, number, number] = [0, 0, 0];
          let axisOrder = 'XYZ';
          let dof: string[] = [];

          while (i < lines.length) {
            const l = lines[i].trim();
            if (l === 'end') break;
            const parts = l.split(/\s+/);
            switch (parts[0]) {
              case 'name':
                name = parts[1];
                break;
              case 'direction':
                direction = [parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3])];
                break;
              case 'length':
                length = parseFloat(parts[1]);
                break;
              case 'axis':
                axis = [parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3])];
                axisOrder = parts[4] || 'XYZ';
                break;
              case 'dof':
                dof = parts.slice(1);
                break;
            }
            i++;
          }

          if (name) {
            bones.set(name, { name, direction, length, axis, axisOrder, dof });
          }
        }
        i++;
      }
      continue;
    }

    if (line === ':hierarchy') {
      i++;
      let inBlock = false;
      while (i < lines.length) {
        const hLine = lines[i].trim();
        if (hLine === 'begin') { inBlock = true; i++; continue; }
        if (hLine === 'end') break;
        if (inBlock) {
          const parts = hLine.split(/\s+/);
          const parent = parts[0];
          const children = parts.slice(1);
          hierarchy.set(parent, children);
        }
        i++;
      }
      i++;
      continue;
    }

    i++;
  }

  return { lengthScale, root, bones, hierarchy };
}
